import { useSyncExternalStore } from 'react';
import type { VoiceOption, VoiceStatus } from '@bingo/shared';
import { loadVoices, pickVoice, portugueseVoices, toVoiceOptions, unlockAudio } from './speech.js';

/**
 * Estado da voz deste aparelho, como **store externa imutável**.
 *
 * Mesmo contrato do `session.ts`, e pelo mesmo motivo: a versão anterior
 * guardava `unlocked` numa variável de closure lida por getter durante a
 * renderização. Isso não é reativo — a tela só atualizava por acaso, quando
 * outro estado mudava junto. Aqui cada mudança publica um **snapshot novo e
 * congelado**, que é o que o `useSyncExternalStore` exige para detectar a
 * mudança.
 */

export interface VoiceState {
  status: VoiceStatus;
  /** Vozes do aparelho, já no formato do protocolo. */
  voices: VoiceOption[];
  /** `voiceURI` escolhido para falar. */
  selectedVoiceURI: string | null;
  /** O gesto do usuário já liberou o áudio? */
  unlocked: boolean;
}

let state: VoiceState = Object.freeze({
  status: 'LOCKED' as VoiceStatus,
  voices: [],
  selectedVoiceURI: null,
  unlocked: false,
});

const listeners = new Set<() => void>();
/** Guardamos os objetos nativos: só eles servem para `utterance.voice`. */
let nativeVoices: SpeechSynthesisVoice[] = [];

function publish(next: Partial<VoiceState>): void {
  state = Object.freeze({ ...state, ...next });
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const snapshot = () => state;

export function useVoiceState(): VoiceState {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

export function voiceSnapshot(): VoiceState {
  return state;
}

/** Voz nativa correspondente à escolha atual. */
export function currentNativeVoice(): SpeechSynthesisVoice | null {
  return pickVoice(nativeVoices, state.selectedVoiceURI);
}

/**
 * Carrega as vozes e recalcula o status.
 *
 * Chamado no boot e de novo depois do desbloqueio: em alguns navegadores a
 * lista só fica completa depois do primeiro gesto.
 */
export async function refreshVoices(preferredUri?: string | null): Promise<VoiceState> {
  nativeVoices = await loadVoices();

  const chosen = pickVoice(nativeVoices, preferredUri ?? state.selectedVoiceURI);
  const semPortugues = portugueseVoices(nativeVoices).length === 0;

  publish({
    voices: toVoiceOptions(nativeVoices),
    selectedVoiceURI: chosen?.voiceURI ?? null,
    // Sem voz em português o problema NÃO se resolve tocando na TV: a saída é
    // narrar pelo celular. Por isso é um status próprio, e não "bloqueado".
    status: !state.unlocked ? 'LOCKED' : semPortugues ? 'NO_PT_VOICE' : 'READY',
  });

  return state;
}

/**
 * Libera o áudio a partir do gesto do usuário.
 *
 * Síncrono de propósito: `unlockAudio()` precisa rodar na mesma pilha do
 * clique. O recarregamento das vozes vem depois, já fora do gesto.
 */
export function unlockFromGesture(): { audioContext: boolean; speech: boolean } {
  const result = unlockAudio();

  publish({ unlocked: true, status: state.voices.length === 0 ? 'LOCKED' : state.status });
  void refreshVoices();

  return result;
}

/** Troca a voz escolhida (vinda dos ajustes do evento). */
export function selectVoice(voiceURI: string | null): void {
  const chosen = pickVoice(nativeVoices, voiceURI);
  publish({ selectedVoiceURI: chosen?.voiceURI ?? null });
}

/** Só para os testes: devolve a store ao estado inicial. */
export function resetVoiceStore(): void {
  nativeVoices = [];
  state = Object.freeze({ status: 'LOCKED', voices: [], selectedVoiceURI: null, unlocked: false });
  for (const listener of listeners) listener();
}
